import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { BadgeCheck, Sparkles } from 'lucide-react'
import { auth } from '../firebase'
import TenantService from '../services/firebase/tenants'

export default function SignupProYearly() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match')
      }

      const credential = await createUserWithEmailAndPassword(auth, formData.email, formData.password)
      const createdUser = credential.user

      await createdUser.getIdToken(true)

      const tenantResult = await TenantService.createTenantForUser(createdUser, 'Professional', {
        billingCycle: 'yearly',
        planType: 'pro_yearly'
      })

      if (tenantResult.error) {
        throw new Error(tenantResult.error.message || 'Failed to create your workspace')
      }

      navigate('/dashboard', { replace: true })
    } catch (err) {
      console.error('Pro yearly signup error:', err)
      setError(err.message || 'Unable to create your account. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-blue-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-700 to-indigo-500 text-white p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6" />
              <span className="text-sm uppercase tracking-[0.2em] font-semibold">Pro Yearly</span>
            </div>
            <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold">Best Value</span>
          </div>
          <h1 className="text-2xl font-bold">Welcome to the Professional Yearly Plan!</h1>
          <p className="text-sm text-white/90 mt-2">Create your account and unlock a full year of professional tools.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 flex items-center gap-2">
            <BadgeCheck className="h-4 w-4" />
            Best value for long-term growth.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              name="password"
              required
              minLength={6}
              value={formData.password}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Create a secure password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              required
              minLength={6}
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Confirm your password"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-gradient-to-r from-blue-700 to-indigo-500 text-white py-3 font-semibold hover:from-blue-800 hover:to-indigo-600 disabled:opacity-60"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  )
}
